import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  Target,
  Dumbbell,
  Zap,
  Heart,
  Flame,
  Trophy,
  ChevronRight,
  Check
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const LOGO_URL = '/logo.webp';

const fitnessGoals = [
  { 
    id: 'weight_loss', 
    label: 'Weight Loss', 
    description: 'Burn fat and slim down',
    icon: Flame,
    color: 'orange'
  },
  { 
    id: 'muscle_building', 
    label: 'Muscle Building', 
    description: 'Build lean muscle mass',
    icon: Dumbbell,
    color: 'blue'
  },
  { 
    id: 'endurance', 
    label: 'Endurance', 
    description: 'Improve stamina and cardio',
    icon: Heart,
    color: 'green'
  },
  { 
    id: 'general_fitness', 
    label: 'General Fitness', 
    description: 'Overall health and wellness',
    icon: Target,
    color: 'purple'
  },
  { 
    id: 'strength', 
    label: 'Strength', 
    description: 'Get stronger and more powerful',
    icon: Zap,
    color: 'red'
  }
];

const experienceLevels = [
  { 
    id: 'beginner', 
    label: 'Athlete', 
    description: 'New to fitness or getting back into it'
  },
  { 
    id: 'intermediate', 
    label: 'Champion', 
    description: 'Consistent training for 6+ months'
  },
  { 
    id: 'advanced', 
    label: 'Warrior', 
    description: 'Years of training experience'
  }
];

export const OnboardingPage = () => {
  const navigate = useNavigate();
  const { api, user, updateProfile } = useAuth();
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [experienceLevel, setExperienceLevel] = useState('');
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  const toggleGoal = (goalId) => {
    setSelectedGoals(prev => 
      prev.includes(goalId) 
        ? prev.filter(g => g !== goalId)
        : [...prev, goalId]
    );
  };

  const handleContinue = () => {
    if (step === 1 && selectedGoals.length === 0) {
      toast.error('Please select at least one goal');
      return;
    }
    if (step === 1) {
      setStep(2);
    }
  };

  const handleComplete = async () => {
    if (!experienceLevel) {
      toast.error('Please select your experience level');
      return;
    }

    setSaving(true);
    try {
      await updateProfile({
        fitness_goals: selectedGoals,
        experience_level: experienceLevel,
        onboarding_completed: true
      });
      toast.success('Profile setup complete!');
      navigate('/dashboard');
    } catch (err) {
      toast.error('Failed to save preferences');
    }
    setSaving(false);
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      await updateProfile({
        onboarding_completed: true
      });
      navigate('/dashboard');
    } catch (err) {
      navigate('/dashboard');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-black flex flex-col" data-testid="onboarding-page">
      {/* Header */}
      <header className="border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="Ascend & Surpass" className="h-12 w-12" />
          <button 
            onClick={handleSkip}
            className="text-muted-foreground hover:text-white transition-colors text-sm"
            data-testid="skip-onboarding"
          >
            Skip for now
          </button>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-6 py-12 max-w-3xl">
        {/* Progress Indicator */}
        <div className="flex items-center justify-center gap-3 mb-12">
          <div className={`h-2 w-24 ${step >= 1 ? 'bg-primary' : 'bg-border'} transition-colors`} />
          <div className={`h-2 w-24 ${step >= 2 ? 'bg-primary' : 'bg-border'} transition-colors`} />
        </div>

        {/* Step 1: Goals */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <div className="text-center mb-10">
              <h1 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-wider mb-4">
                What Are Your <span className="text-primary">Goals?</span>
              </h1>
              <p className="text-muted-foreground text-lg">
                Select all that apply. We'll help you find the right programs.
              </p>
            </div>

            <div className="grid gap-4 mb-10">
              {fitnessGoals.map((goal) => {
                const Icon = goal.icon;
                const isSelected = selectedGoals.includes(goal.id);
                
                return (
                  <motion.button
                    key={goal.id}
                    onClick={() => toggleGoal(goal.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid={`goal-${goal.id}`}
                    className={`flex items-center gap-4 p-5 border transition-all text-left
                      ${isSelected 
                        ? 'bg-primary/10 border-primary' 
                        : 'bg-card border-border hover:border-primary/50'}`}
                  >
                    <div className={`h-14 w-14 flex items-center justify-center
                      ${isSelected ? 'bg-primary' : 'bg-white/10'}`}>
                      <Icon className={`h-7 w-7 ${isSelected ? 'text-black' : 'text-white'}`} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl text-white uppercase tracking-wider">
                        {goal.label}
                      </h3>
                      <p className="text-muted-foreground text-sm">{goal.description}</p>
                    </div>
                    <div className={`h-8 w-8 border-2 flex items-center justify-center
                      ${isSelected 
                        ? 'bg-primary border-primary' 
                        : 'border-white/30'}`}>
                      {isSelected && <Check className="h-5 w-5 text-black" />}
                    </div>
                  </motion.button>
                );
              })}
            </div>

            <Button 
              onClick={handleContinue}
              className="w-full h-14 bg-primary text-primary-foreground font-heading text-lg tracking-wider uppercase hover:bg-primary/90"
              disabled={selectedGoals.length === 0}
              data-testid="continue-to-experience"
            >
              Continue
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        )}

        {/* Step 2: Experience Level */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <div className="text-center mb-10">
              <h1 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-wider mb-4">
                Your <span className="text-primary">Experience</span> Level
              </h1>
              <p className="text-muted-foreground text-lg">
                This helps us recommend the right workout intensity.
              </p>
            </div>

            <div className="grid gap-4 mb-10">
              {experienceLevels.map((level) => {
                const isSelected = experienceLevel === level.id;
                
                return (
                  <motion.button
                    key={level.id}
                    onClick={() => setExperienceLevel(level.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid={`experience-${level.id}`}
                    className={`flex items-center gap-4 p-5 border transition-all text-left
                      ${isSelected 
                        ? 'bg-primary/10 border-primary' 
                        : 'bg-card border-border hover:border-primary/50'}`}
                  >
                    <div className={`h-14 w-14 flex items-center justify-center font-heading text-2xl
                      ${isSelected ? 'bg-primary text-black' : 'bg-white/10 text-white'}`}>
                      {level.id === 'beginner' && '1'}
                      {level.id === 'intermediate' && '2'}
                      {level.id === 'advanced' && '3'}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-xl text-white uppercase tracking-wider">
                        {level.label}
                      </h3>
                      <p className="text-muted-foreground text-sm">{level.description}</p>
                    </div>
                    <div className={`h-8 w-8 rounded-full border-2 flex items-center justify-center
                      ${isSelected 
                        ? 'bg-primary border-primary' 
                        : 'border-white/30'}`}>
                      {isSelected && <Check className="h-5 w-5 text-black" />}
                    </div>
                  </motion.button>
                );
              })}
            </div>

            <div className="flex gap-4">
              <Button 
                onClick={() => setStep(1)}
                variant="outline"
                className="flex-1 h-14 border-white/20 text-white hover:bg-white/10 font-heading tracking-wider uppercase"
              >
                Back
              </Button>
              <Button 
                onClick={handleComplete}
                className="flex-1 h-14 bg-primary text-primary-foreground font-heading text-lg tracking-wider uppercase hover:bg-primary/90"
                disabled={!experienceLevel || saving}
                data-testid="complete-onboarding"
              >
                {saving ? (
                  <div className="animate-spin h-5 w-5 border-2 border-black border-t-transparent rounded-full" />
                ) : (
                  <>
                    <Trophy className="mr-2 h-5 w-5" />
                    Let's Go!
                  </>
                )}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Selected Goals Summary (Step 2) */}
        {step === 2 && selectedGoals.length > 0 && (
          <div className="mt-8 p-4 bg-card border border-border">
            <p className="text-muted-foreground text-sm mb-2">Your selected goals:</p>
            <div className="flex flex-wrap gap-2">
              {selectedGoals.map(goalId => {
                const goal = fitnessGoals.find(g => g.id === goalId);
                return (
                  <span 
                    key={goalId}
                    className="bg-primary/20 text-primary px-3 py-1 text-sm font-heading uppercase tracking-wider"
                  >
                    {goal?.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
